/**
 * actions/users.ts
 * desc: users actions
 * note: responsible for authentication and user-principle information
 */

import apiUrls from '../../settings/api-urls';
import request, { requestRaw } from '../../utilities/request';
import { interpolate } from '../../utilities';
import type { AppThunk } from '../index';
import type { UserDto, WhoAmIDto } from '../../types/api/users';
import type { RoleRepresentation } from '../../types/keycloak/roles';
import type { CredentialRepresentation, IdentityProviderRepresentation, UserRepresentation } from '../../types/keycloak/users';
import type { KeycloakUser, KeycloakUserDetail } from '../reducers/users';

const keycloakApiUrl = window.Ym.KEYCLOAK_ADMIN_URL ?? window.Ym.KEYCLOAK_CONFIG?.url;
const realm = window.Ym.KEYCLOAK_CONFIG?.realm;
const keycloakClientId = window.Ym.KEYCLOAK_CONFIG?.clientId;

// the realm roles of one user, as GET_USERS_ROLES_SUCCESS carries them
export interface UserRoles {
    userId: string;
    roles: RoleRepresentation[];
}


//*************************************************
//*************   CLEAR Users   *******************
//*************************************************
export function clearUsers() {
    return {
        type: 'CLEAR_USERS',
    } as const;
}



//*************************************************
//*****   CREATE Execeute User Actions  ***********
//*************************************************
export function createUserExecuteActions(userId: string, lifespan: number, payload: string[]): AppThunk<Promise<unknown>> {
    return (dispatch) => {
        const path = interpolate(apiUrls.KEYCLOAK_USER_EXECUTE_ACTIONS, { realm, userId });
        const url = `${keycloakApiUrl}${path}?lifespan=${lifespan}&client_id=${keycloakClientId}`;
        const opts = {
            method:'PUT',
            body: JSON.stringify(payload),
        };

        dispatch(_createExecuteUserActionsRequest());

        return request(url, { ...opts })
            .then(result => {
                dispatch(_createExecuteUserActionsSuccess(result));
                return result;
            })
            .catch(err => {
                dispatch(_createExecuteUserActionsError());
                throw err;
            });
    };
}

function _createExecuteUserActionsError() {
    return {
        type: 'CREATE_USER_EXECUTE_ACTIONS_ERROR',
    } as const;
}

function _createExecuteUserActionsRequest() {
    return {
        type: 'CREATE_USER_EXECUTE_ACTIONS_REQUEST',
    } as const;
}

function _createExecuteUserActionsSuccess(payload: unknown) {
    return {
        type: 'CREATE_USER_EXECUTE_ACTIONS_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   CREATE User (Instance)   ****************
//*************************************************
export function createUser(payload: UserRepresentation): AppThunk<Promise<{ id: string }>> {
    return (dispatch) => {
        const path = interpolate(apiUrls.KEYCLOAK_USERS, { realm });
        const url = `${keycloakApiUrl}${path}`;
        const opts = {
            method:'POST',
            body: JSON.stringify(payload),
        };

        dispatch(_createUserRequest());

        return requestRaw(url, { ...opts })
            .then(results => {
                // Keycloak answers 201 with the new user's URL in the Location header; the id is its last segment
                const responseLocation = String(results.headers.get('location'));
                const id = responseLocation.slice(responseLocation.lastIndexOf('/') + 1);
                dispatch(_createUserSuccess({ id }));
                return { id };
            })
            .catch(err => {
                dispatch(_createUserError());
                throw err;
            });
    };
}

function _createUserError() {
    return {
        type: 'CREATE_USER_ERROR',
    } as const;
}

function _createUserRequest() {
    return {
        type: 'CREATE_USER_REQUEST',
    } as const;
}

function _createUserSuccess(payload: { id: string }) {
    return {
        type: 'CREATE_USER_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   CREATE User Roles (Instance)   **********
//*************************************************
export function createUserRoles(opts: { userId: string }, payload: RoleRepresentation[]): AppThunk<Promise<RoleRepresentation[]>> {
    return (dispatch) => {
        const path = interpolate(apiUrls.KEYCLOAK_USER_ROLE_MAPPINGS, { ...opts, realm });
        const url = `${keycloakApiUrl}${path}`;
        const fetchOpts = {
            method:'POST',
            body: JSON.stringify(payload),
        };

        dispatch(_createUserRolesRequest());

        return request(url, fetchOpts)
            .then(() => {
                dispatch(_createUserRolesSuccess(payload));
                return payload;
            })
            .catch(err => {
                dispatch(_createUserRolesError());
                throw err;
            });
    };
}

function _createUserRolesError() {
    return {
        type: 'CREATE_USER_ROLES_ERROR',
    } as const;
}

function _createUserRolesRequest() {
    return {
        type: 'CREATE_USER_ROLES_REQUEST',
    } as const;
}

function _createUserRolesSuccess(payload: RoleRepresentation[]) {
    return {
        type: 'CREATE_USER_ROLES_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   DELETE User (Instance)   ****************
//*************************************************
export function deleteUser(keyCloakUserId: string, dbUserId?: string): AppThunk<Promise<string>> {
    return (dispatch) => {
        const promiseArr: Promise<unknown>[] = [];
        const path = interpolate(apiUrls.KEYCLOAK_USER, { realm, userId: keyCloakUserId });
        const url = `${keycloakApiUrl}${path}`;
        const opts = {
            method:'DELETE',
        };

        dispatch(_deleteUserRequest());

        promiseArr.push(request(url, opts));
        if (dbUserId) {
            promiseArr.push(request(interpolate(apiUrls.USER, { userId: dbUserId }), opts));
        }

        return Promise.all(promiseArr)
            .then(() => {
                dispatch(_deleteUserSuccess(keyCloakUserId));
                return keyCloakUserId;
            })
            .catch(err => {
                dispatch(_deleteUserError());
                throw err;
            });
    };
}

function _deleteUserError() {
    return {
        type: 'DELETE_USER_ERROR',
    } as const;
}

function _deleteUserRequest() {
    return {
        type: 'DELETE_USER_REQUEST',
    } as const;
}

function _deleteUserSuccess(payload: string) {
    return {
        type: 'DELETE_USER_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   DELETE User Roles (Instance)   **********
//*************************************************
export function deleteUserRoles(opts: { userId: string }, payload: RoleRepresentation[]): AppThunk<Promise<RoleRepresentation[]>> {
    return (dispatch) => {
        const path = interpolate(apiUrls.KEYCLOAK_USER_ROLE_MAPPINGS, { ...opts, realm });
        const url = `${keycloakApiUrl}${path}`;
        const fetchOpts = {
            method:'DELETE',
            body: JSON.stringify(payload),
        };

        dispatch(_deleteUserRolesRequest());

        return request(url, fetchOpts)
            .then(() => {
                dispatch(_deleteUserRolesSuccess(payload));
                return payload;
            })
            .catch(err => {
                dispatch(_deleteUserRolesError());
                throw err;
            });
    };
}

function _deleteUserRolesError() {
    return {
        type: 'DELETE_USER_ROLES_ERROR',
    } as const;
}

function _deleteUserRolesRequest() {
    return {
        type: 'DELETE_USER_ROLES_REQUEST',
    } as const;
}

function _deleteUserRolesSuccess(payload: RoleRepresentation[]) {
    return {
        type: 'DELETE_USER_ROLES_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   GET Identity Providers (List)   *********
//*************************************************
export function getIdentityProviders(): AppThunk<Promise<IdentityProviderRepresentation[]>> {
    return (dispatch) => {
        const url = `${keycloakApiUrl}${interpolate(apiUrls.KEYCLOAK_IDENTITY_PROVIDERS, { realm })}`;

        dispatch(_getIdentityProvidersRequest());

        return request<IdentityProviderRepresentation[]>(url)
            .then(results => {
                dispatch(_getIdentityProvidersSuccess(results));
                return results;
            })
            .catch(err => {
                dispatch(_getIdentityProvidersError());
                throw err;
            });
    };
}

function _getIdentityProvidersError() {
    return {
        type: 'GET_IDENTITY_PROVIDERS_ERROR',
    } as const;
}

function _getIdentityProvidersRequest() {
    return {
        type: 'GET_IDENTITY_PROVIDERS_REQUEST',
    } as const;
}

function _getIdentityProvidersSuccess(payload: IdentityProviderRepresentation[]) {
    return {
        type: 'GET_IDENTITY_PROVIDERS_SUCCESS',
        payload
    } as const;
}



//*************************************************
//*****   GET User (Instance)   *******************
//*************************************************
// with a userId: the Keycloak user plus the tier0-core user matched by email; without: the signed-in user
export function getUser(userId?: string): AppThunk<Promise<WhoAmIDto | KeycloakUserDetail>> {
    return (dispatch) => {
        const promiseArr: Promise<unknown>[] = [];
        dispatch(_getUserRequest());

        const url = userId ? `${keycloakApiUrl}${interpolate(apiUrls.KEYCLOAK_USER, { userId, realm })}` : apiUrls.USER_ME;

        promiseArr.push(request(url));

        if (userId) {
            promiseArr.push(request(apiUrls.USERS));
        }

        return Promise.all(promiseArr)
            .then((results) => {
                const dbUsers = results[1] as UserDto[] | undefined;
                const userInfo = results[0] as WhoAmIDto | KeycloakUserDetail;

                if (dbUsers) {
                    const foundDbUser = dbUsers.find(user => user.userName === userInfo.email);
                    if (foundDbUser && userId) {
                        (userInfo as KeycloakUserDetail).dbUser = foundDbUser;
                    }
                }

                dispatch(_getUserSuccess(userInfo, !!userId));
                return userInfo;
            })
            .catch(err => {
                dispatch(_getUserError());
                throw err;
            });
    };
}

function _getUserError() {
    return {
        type: 'GET_USER_ERROR',
    } as const;
}

function _getUserRequest() {
    return {
        type: 'GET_USER_REQUEST',
    } as const;
}

// isKeyCloak tells the two payload shapes apart: a Keycloak user for the detail page, the signed-in user otherwise
function _getUserSuccess(payload: WhoAmIDto | KeycloakUserDetail, isKeyCloak: boolean) {
    return {
        type: 'GET_USER_SUCCESS',
        payload,
        isKeyCloak
    } as const;
}



//*************************************************
//*****   GET Users (db)        *******************
//*************************************************
export function getUsersDb(): AppThunk<Promise<UserDto[]>> {
    return (dispatch) => {
        dispatch(_getUsersDbRequest());

        return request<UserDto[]>(interpolate(apiUrls.USERS))
            .then((payload) => {
                dispatch(_getUsersDbSuccess(payload));
                return payload;
            })
            .catch(err => {
                dispatch(_getUsersDbError());
                throw err;
            });
    };
}

function _getUsersDbError() {
    return {
        type: 'GET_USERS_DB_ERROR',
    } as const;
}

function _getUsersDbRequest() {
    return {
        type: 'GET_USERS_DB_REQUEST',
    } as const;
}

function _getUsersDbSuccess(payload: UserDto[]) {
    return {
        type: 'GET_USERS_DB_SUCCESS',
        payload
    } as const;
}



//*************************************************
//**********   GET Users (Collection)   ***********
//*************************************************
export function getUsers(params: string): AppThunk<Promise<KeycloakUser[]>> {
    return (dispatch) => {
        dispatch(_getUsersRequest());

        const path = `${interpolate(apiUrls.KEYCLOAK_USERS, { realm })}${params}`;
        const url = `${keycloakApiUrl}${path}`;

        return Promise.all([
            // @ts-expect-error — the query string is passed where fetch options belong; fetch ignores it, so
            // the call works and is kept as it always was
            request<UserRepresentation[]>(url, params),
            // @ts-expect-error — same: spreading the query string yields index keys fetch ignores
            request<UserDto[]>(apiUrls.USERS, { ...params })
        ])
            .then(results => {
                const keycloakUsers = results[0];
                const dbUsers = results[1];
                const combined: KeycloakUser[] = keycloakUsers.map(keycloakUser => {
                    const dbUser = dbUsers.find(item => item.userName === keycloakUser.email);
                    return {
                        ...keycloakUser,
                        dbUser: dbUser || null
                    };
                });

                dispatch(_getUsersSuccess(combined));
                return combined;
            })
            .catch(err => {
                dispatch(_getUsersError());
                throw err;
            });
    };
}

function _getUsersError() {
    return {
        type: 'GET_USERS_ERROR',
    } as const;
}

function _getUsersRequest() {
    return {
        type: 'GET_USERS_REQUEST',
    } as const;
}

function _getUsersSuccess(payload: KeycloakUser[]) {
    return {
        type: 'GET_USERS_SUCCESS',
        payload
    } as const;
}



//*************************************************
//*****   GET User Roles (Collection)   ***********
//*************************************************
export function getUsersRoles(usersIds: string[]): AppThunk<Promise<RoleRepresentation[][]>> {

    return (dispatch) => {
        const promiseList: Promise<RoleRepresentation[]>[] = [];

        dispatch(_getUsersRolesRequest());

        usersIds.forEach(id => {
            const path = `${interpolate(apiUrls.KEYCLOAK_USER_ROLE_MAPPINGS_COMPOSITE, { realm, userId: id })}`;
            const url = `${keycloakApiUrl}${path}`;
            promiseList.push(request<RoleRepresentation[]>(url));
        });

        return Promise.all(promiseList)
            .then(results => {
                dispatch(_getUsersRolesSuccess(results.map((item, index) => ({ userId: usersIds[index], roles: item }))));
                return results;
            })
            .catch(err => {
                dispatch(_getUsersRolesError());
                throw err;
            });
    };
}

function _getUsersRolesError() {
    return {
        type: 'GET_USERS_ROLES_ERROR',
    } as const;
}

function _getUsersRolesRequest() {
    return {
        type: 'GET_USERS_ROLES_REQUEST',
    } as const;
}

function _getUsersRolesSuccess(payload: UserRoles[]) {
    return {
        type: 'GET_USERS_ROLES_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   UPDATE User (Instance)   ****************
//*************************************************
// a failure rejects with the per-request errors (Keycloak first, tier0-core second), not an Error
export function updateUser(keycloakOpts: { userId: string }, keycloakPayload: Partial<UserRepresentation>, dbOpts?: { userId: string } | null, dbPayload?: UserDto | null): AppThunk<Promise<Partial<UserRepresentation>>> {
    return (dispatch) => {
        const promiseArr: Promise<unknown>[] = [];
        const path = interpolate(apiUrls.KEYCLOAK_USER, { ...keycloakOpts, realm });
        const url = `${keycloakApiUrl}${path}`;
        const fetchOpts = {
            method:'PUT',
            body: JSON.stringify(keycloakPayload),
        };

        dispatch(_updateUserRequest());

        promiseArr.push(request(url, fetchOpts));
        if (dbOpts && dbPayload) {
            promiseArr.push(request(interpolate(apiUrls.USER, { ...dbOpts }), {
                method:'POST',
                body: JSON.stringify(dbPayload),
            }));
        }

        return Promise.allSettled(promiseArr)
            .then((results) => {
                let isAnyError = false;
                const errorsArr = results.map(item => {
                    if (item.status === 'rejected') {
                        isAnyError = true;
                        return item.reason;
                    }
                    return null;
                });

                if (isAnyError) {
                    throw errorsArr;
                } else {
                    dispatch(_updateUserSuccess(keycloakPayload));
                    return keycloakPayload;
                }
            })
            .catch(err => {
                dispatch(_updateUserError());
                throw err;
            });
    };
}

function _updateUserError() {
    return {
        type: 'UPDATE_USER_ERROR',
    } as const;
}

function _updateUserRequest() {
    return {
        type: 'UPDATE_USER_REQUEST',
    } as const;
}

function _updateUserSuccess(payload: Partial<UserRepresentation>) {
    return {
        type: 'UPDATE_USER_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   UPDATE User (Instance)   ****************
//*************************************************
export function updateUserMe(opts: { userId: string }, payload: UserDto): AppThunk<Promise<UserDto>> {
    return (dispatch) => {

        dispatch(_updateUserMeRequest());

        return request<UserDto>(interpolate(apiUrls.USER, { ...opts }), {
            method:'POST',
            body: JSON.stringify(payload),
        })
            .then((results) => {
                dispatch(_updateUserMeSuccess(results?.id ? results : payload));
                return results;
            })
            .catch(err => {
                dispatch(_updateUserMeError());
                throw err;
            });
    };
}

function _updateUserMeError() {
    return {
        type: 'UPDATE_USER_ME_ERROR',
    } as const;
}

function _updateUserMeRequest() {
    return {
        type: 'UPDATE_USER_ME_REQUEST',
    } as const;
}

function _updateUserMeSuccess(payload: UserDto) {
    return {
        type: 'UPDATE_USER_ME_SUCCESS',
        payload,
    } as const;
}



//*************************************************
//*****   UPDATE User Password (Instance)   *******
//*************************************************
export function updateUserPassword(opts: { userId: string }, payload: CredentialRepresentation): AppThunk<Promise<CredentialRepresentation>> {
    return (dispatch) => {
        const path = interpolate(apiUrls.KEYCLOAK_USER_RESET_PASSWORD, { ...opts, realm });
        const url = `${keycloakApiUrl}${path}`;
        const fetchOpts = {
            method:'PUT',
            body: JSON.stringify(payload),
        };

        dispatch(_updateUserPasswordRequest());

        return request(url, fetchOpts)
            .then(() => {
                dispatch(_updateUserPasswordSuccess(payload));
                return payload;
            })
            .catch(err => {
                dispatch(_updateUserPasswordError());
                throw err;
            });
    };
}

function _updateUserPasswordError() {
    return {
        type: 'UPDATE_USER_PASSWORD_ERROR',
    } as const;
}

function _updateUserPasswordRequest() {
    return {
        type: 'UPDATE_USER_PASSWORD_REQUEST',
    } as const;
}

function _updateUserPasswordSuccess(payload: CredentialRepresentation) {
    return {
        type: 'UPDATE_USER_PASSWORD_SUCCESS',
        payload,
    } as const;
}

export type UsersAction =
    | ReturnType<typeof clearUsers>
    | ReturnType<typeof _createExecuteUserActionsError>
    | ReturnType<typeof _createExecuteUserActionsRequest>
    | ReturnType<typeof _createExecuteUserActionsSuccess>
    | ReturnType<typeof _createUserError>
    | ReturnType<typeof _createUserRequest>
    | ReturnType<typeof _createUserSuccess>
    | ReturnType<typeof _createUserRolesError>
    | ReturnType<typeof _createUserRolesRequest>
    | ReturnType<typeof _createUserRolesSuccess>
    | ReturnType<typeof _deleteUserError>
    | ReturnType<typeof _deleteUserRequest>
    | ReturnType<typeof _deleteUserSuccess>
    | ReturnType<typeof _deleteUserRolesError>
    | ReturnType<typeof _deleteUserRolesRequest>
    | ReturnType<typeof _deleteUserRolesSuccess>
    | ReturnType<typeof _getIdentityProvidersError>
    | ReturnType<typeof _getIdentityProvidersRequest>
    | ReturnType<typeof _getIdentityProvidersSuccess>
    | ReturnType<typeof _getUserError>
    | ReturnType<typeof _getUserRequest>
    | ReturnType<typeof _getUserSuccess>
    | ReturnType<typeof _getUsersDbError>
    | ReturnType<typeof _getUsersDbRequest>
    | ReturnType<typeof _getUsersDbSuccess>
    | ReturnType<typeof _getUsersError>
    | ReturnType<typeof _getUsersRequest>
    | ReturnType<typeof _getUsersSuccess>
    | ReturnType<typeof _getUsersRolesError>
    | ReturnType<typeof _getUsersRolesRequest>
    | ReturnType<typeof _getUsersRolesSuccess>
    | ReturnType<typeof _updateUserError>
    | ReturnType<typeof _updateUserRequest>
    | ReturnType<typeof _updateUserSuccess>
    | ReturnType<typeof _updateUserMeError>
    | ReturnType<typeof _updateUserMeRequest>
    | ReturnType<typeof _updateUserMeSuccess>
    | ReturnType<typeof _updateUserPasswordError>
    | ReturnType<typeof _updateUserPasswordRequest>
    | ReturnType<typeof _updateUserPasswordSuccess>;
